import { useEffect, useMemo, useRef, useState } from "react";
import type { Coffee } from "../types";
import { WORLD_PATH, WORLD_VIEWBOX, project } from "../lib/worldPath";

type Mode = "origin" | "roaster";

interface Pin {
  x: number;
  y: number;
  count: number;
  labels: string[];
}

const { w: VW, h: VH } = WORLD_VIEWBOX;
const MIN_K = 1;
const MAX_K = 9;

function coord(c: Coffee, mode: Mode): { lat: number; lng: number } | null {
  const lat = mode === "origin" ? c.origin_lat : c.roaster_lat;
  const lng = mode === "origin" ? c.origin_lng : c.roaster_lng;
  return lat == null || lng == null ? null : { lat, lng };
}

function label(c: Coffee, mode: Mode): string {
  if (mode === "origin") return [c.region, c.country].filter(Boolean).join(", ") || c.country;
  return c.roaster_city || c.roaster;
}

// Group coffees that resolve to (nearly) the same point into one dot, so a place
// with several coffees reads as a single, larger pin rather than a smear.
function buildPins(coffees: Coffee[], mode: Mode): Pin[] {
  const map = new Map<string, Pin>();
  for (const c of coffees) {
    const ll = coord(c, mode);
    if (!ll) continue;
    const key = `${Math.round(ll.lat * 2) / 2},${Math.round(ll.lng * 2) / 2}`;
    const lbl = label(c, mode);
    const p = map.get(key);
    if (p) {
      p.count += 1;
      if (lbl && !p.labels.includes(lbl)) p.labels.push(lbl);
    } else {
      const { x, y } = project(ll.lng, ll.lat);
      map.set(key, { x, y, count: 1, labels: lbl ? [lbl] : [] });
    }
  }
  // Larger dots drawn first so smaller ones stay clickable on top.
  return [...map.values()].sort((a, b) => b.count - a.count);
}

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

export default function WorldMap({ coffees }: { coffees: Coffee[] }) {
  const [mode, setMode] = useState<Mode>("origin");
  const [active, setActive] = useState<Pin | null>(null);

  // Pan/zoom view: a point (x,y) in map space renders at (x*k + tx, y*k + ty).
  // Dot radii stay in this outer viewBox space (constant on screen) so zooming
  // spreads clustered pins apart instead of just magnifying the whole clump.
  const [view, setView] = useState({ k: 1, tx: 0, ty: 0 });
  const svgRef = useRef<SVGSVGElement | null>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const dragged = useRef(false);

  const pins = useMemo(() => buildPins(coffees, mode), [coffees, mode]);
  const totalCoffees = useMemo(() => pins.reduce((n, p) => n + p.count, 0), [pins]);
  const radius = (count: number) => 3 + Math.sqrt(count) * 1.5;

  const clampView = (k: number, tx: number, ty: number) => ({
    k,
    tx: clamp(tx, VW * (1 - k), 0),
    ty: clamp(ty, VH * (1 - k), 0),
  });

  // Map a client point to outer-viewBox coordinates (0..VW, 0..VH).
  const toVB = (clientX: number, clientY: number) => {
    const r = svgRef.current!.getBoundingClientRect();
    return { x: ((clientX - r.left) / r.width) * VW, y: ((clientY - r.top) / r.height) * VH };
  };

  // Zoom by `factor` keeping the map point under (vx,vy) fixed on screen.
  const zoomAt = (vx: number, vy: number, factor: number) => {
    setView((v) => {
      const k = clamp(v.k * factor, MIN_K, MAX_K);
      if (k === v.k) return v;
      const ratio = k / v.k;
      return clampView(k, vx - (vx - v.tx) * ratio, vy - (vy - v.ty) * ratio);
    });
  };

  const reset = () => setView({ k: 1, tx: 0, ty: 0 });
  const zoomButton = (factor: number) => zoomAt(VW / 2, VH / 2, factor);

  // Wheel zoom — attached natively so we can preventDefault (React's onWheel is
  // passive), keeping the page from scrolling while zooming the map.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const { x, y } = toVB(e.clientX, e.clientY);
      zoomAt(x, y, e.deltaY < 0 ? 1.12 : 1 / 1.12);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    dragged.current = false;
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const pts = pointers.current;
    const prev = pts.get(e.pointerId);
    if (!prev) return;
    const cur = { x: e.clientX, y: e.clientY };

    if (pts.size >= 2) {
      // Pinch: zoom around the midpoint by the change in finger distance.
      const others = [...pts.entries()].filter(([id]) => id !== e.pointerId);
      const [, o] = others[0];
      const oldD = Math.hypot(prev.x - o.x, prev.y - o.y) || 1;
      const newD = Math.hypot(cur.x - o.x, cur.y - o.y) || 1;
      const mid = toVB((cur.x + o.x) / 2, (cur.y + o.y) / 2);
      zoomAt(mid.x, mid.y, newD / oldD);
      dragged.current = true;
    } else {
      // Pan by the pointer delta (converted to viewBox units).
      const r = svgRef.current!.getBoundingClientRect();
      const dx = ((cur.x - prev.x) / r.width) * VW;
      const dy = ((cur.y - prev.y) / r.height) * VH;
      if (Math.abs(cur.x - prev.x) + Math.abs(cur.y - prev.y) > 2) dragged.current = true;
      setView((v) => clampView(v.k, v.tx + dx, v.ty + dy));
    }
    pts.set(e.pointerId, cur);
  };

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
  };

  const zoomed = view.k > 1.001;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-end gap-2 text-[11px] uppercase tracking-widest">
          <button
            onClick={() => { setMode("origin"); setActive(null); }}
            className={`pb-0.5 border-b ${mode === "origin" ? "border-ink text-ink" : "border-transparent text-paper-400"}`}
          >
            Origins
          </button>
          <span className="text-paper-300">/</span>
          <button
            onClick={() => { setMode("roaster"); setActive(null); }}
            className={`pb-0.5 border-b ${mode === "roaster" ? "border-ink text-ink" : "border-transparent text-paper-400"}`}
          >
            Roasters
          </button>
        </div>
        <div className="text-[11px] uppercase tracking-widest text-paper-400">
          {pins.length} {mode === "origin" ? "origin" : "roaster"}{pins.length === 1 ? "" : "s"} · {totalCoffees} coffee{totalCoffees === 1 ? "" : "s"}
        </div>
      </div>

      <div className="relative border border-paper-200 bg-paper-50 overflow-hidden">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VW} ${VH}`}
          className={`w-full h-auto block ${zoomed ? "cursor-grab" : ""}`}
          style={{ touchAction: "none" }}
          role="img"
          aria-label={`World map of coffee ${mode === "origin" ? "origins" : "roasters"}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {/* Land silhouette — quiet, no borders, no labels. Scales with zoom. */}
          <g transform={`translate(${view.tx} ${view.ty}) scale(${view.k})`}>
            <path d={WORLD_PATH} fill="#e9e4d9" />
          </g>
          {/* Pins live in outer space with fixed radii, so clusters separate on zoom. */}
          {pins.map((p, i) => {
            const isActive = active === p;
            return (
              <circle
                key={i}
                cx={p.x * view.k + view.tx}
                cy={p.y * view.k + view.ty}
                r={radius(p.count) + (isActive ? 1.5 : 0)}
                fill="#111110"
                fillOpacity={isActive ? 1 : 0.7}
                stroke="#fbfaf7"
                strokeWidth={0.8}
                vectorEffect="non-scaling-stroke"
                className="cursor-pointer transition-[r,fill-opacity]"
                onPointerEnter={() => setActive(p)}
                onPointerLeave={() => setActive((cur) => (cur === p ? null : cur))}
                onClick={() => { if (!dragged.current) setActive((cur) => (cur === p ? null : p)); }}
              >
                <title>{`${p.labels.join(" · ") || "Unknown"} — ${p.count} coffee${p.count === 1 ? "" : "s"}`}</title>
              </circle>
            );
          })}
        </svg>

        {/* Zoom controls — minimal, top-right. */}
        <div className="absolute top-2 right-2 flex flex-col gap-px bg-paper-200 border border-paper-200 text-paper-600">
          <button onClick={() => zoomButton(1.4)} className="w-7 h-7 bg-paper-50 hover:text-ink leading-none text-base" aria-label="Zoom in">+</button>
          <button onClick={() => zoomButton(1 / 1.4)} className="w-7 h-7 bg-paper-50 hover:text-ink leading-none text-base" aria-label="Zoom out">−</button>
          {zoomed ? (
            <button onClick={reset} className="w-7 h-7 bg-paper-50 hover:text-ink leading-none text-[9px] uppercase tracking-wider" aria-label="Reset zoom">Fit</button>
          ) : null}
        </div>
      </div>

      {/* Fixed-height caption line: name + count on hover/tap; hint otherwise. */}
      <div className="h-5 text-sm text-paper-600">
        {active ? (
          <span>
            <span className="text-ink font-medium">{active.labels.join(" · ") || "Unknown"}</span>
            <span className="text-paper-400"> · {active.count} coffee{active.count === 1 ? "" : "s"}</span>
          </span>
        ) : pins.length === 0 ? (
          <span className="text-paper-400">No mapped {mode === "origin" ? "origins" : "roasters"} yet — coordinates fill in as coffees are added or edited.</span>
        ) : (
          <span className="text-paper-400">Scroll or pinch to zoom · drag to pan · hover a pin for the place.</span>
        )}
      </div>
    </div>
  );
}
